import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import TopNav from "../components/TopNav";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import PromoCodeInput from "../components/PromoCodeInput";
import { ArrowRight, Check, Loader2, Zap } from "lucide-react";

const tiers = [
  {
    id: "free",
    name: "Free",
    price: "£0",
    note: "forever",
    description: "Try it out — no card needed.",
    features: [
      "3 listing generations (lifetime)",
      "1 cover project (all 3 panels)",
      "1 AI image generation",
      "Watermarked exports",
    ],
    cta: "Start free",
    highlight: false,
    badge: null,
  },
  {
    id: "solo",
    name: "Solo",
    price: "£14",
    note: "/month",
    description: "For the indie author publishing regularly.",
    features: [
      "25 listing generations / month",
      "10 cover projects / month",
      "15 AI image generations / month",
      "Paperback & hardcover covers",
      "No watermark on exports",
      "Save up to 50 projects",
    ],
    cta: "Choose Solo",
    highlight: false,
    badge: null,
  },
  {
    id: "pro",
    name: "Pro",
    price: "£29",
    note: "/month",
    description: "For serious publishers and power users.",
    features: [
      "Unlimited listing generations",
      "Unlimited cover projects",
      "40 AI image generations / month",
      "Paperback & hardcover covers",
      "No watermark on exports",
      "Unlimited project library",
      "Priority support",
    ],
    cta: "Choose Pro",
    highlight: true,
    badge: "Most popular",
  },
  {
    id: "agency",
    name: "Agency",
    price: "£79",
    note: "/month",
    description: "For ghostwriters, formatters and publishing services.",
    features: [
      "Unlimited everything",
      "100 AI image generations / month",
      "Paperback & hardcover covers",
      "No watermark on exports",
      "Unlimited project library",
      "White-label exports",
      "Priority support + onboarding call",
    ],
    cta: "Choose Agency",
    highlight: false,
    badge: null,
  },
];

const Pricing = () => {
  const { session, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");

  const handleSelect = async (tier) => {
    if (tier === "free") {
      navigate(session ? "/dashboard" : "/signup");
      return;
    }
    if (!session) {
      navigate(`/signup?plan=${tier}`);
      return;
    }
    try {
      setBusy(tier);
      const { data } = await api.post("/billing/create-checkout", { tier });
      window.location.href = data.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not start checkout");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <TopNav />
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase tracking-[0.25em] text-[#534AB7]">Pricing</span>
          <h1
            className="mt-4 text-5xl lg:text-6xl tracking-tight leading-[1.05]"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
          >
            Pick a plan, ship more books.
          </h1>
          <p className="mt-5 text-[#5F5E5A]">No hidden fees. Cancel anytime. All prices in GBP.</p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              data-testid={`pricing-${tier.id}`}
              className={`relative rounded-[12px] bg-white border flex flex-col ${
                tier.highlight
                  ? "border-[#534AB7] shadow-[0_25px_60px_-30px_rgba(83,74,183,0.4)]"
                  : "border-[#D3D1C7]"
              } p-7`}
            >
              {tier.badge && (
                <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-[#534AB7] text-white text-[10px] uppercase tracking-[0.2em]">
                  {tier.badge}
                </span>
              )}
              <div className="text-sm font-semibold text-[#2C2C2A]">{tier.name}</div>
              <p className="mt-1 text-xs text-[#5F5E5A] leading-snug">{tier.description}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span
                  className="text-4xl tracking-tight"
                  style={{ fontFamily: "Outfit, Inter, sans-serif", fontWeight: 500 }}
                >
                  {tier.price}
                </span>
                <span className="text-sm text-[#5F5E5A]">{tier.note}</span>
              </div>
              <ul className="mt-6 space-y-2.5 text-sm flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#1D9E75] mt-0.5 shrink-0" strokeWidth={2} />
                    <span className="text-[#2C2C2A]">{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleSelect(tier.id)}
                disabled={busy === tier.id}
                data-testid={`select-${tier.id}-btn`}
                className={`mt-7 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-[8px] font-medium transition-colors text-sm disabled:opacity-50 ${
                  tier.highlight
                    ? "bg-[#534AB7] text-white hover:bg-[#3C3489]"
                    : "border border-[#D3D1C7] text-[#2C2C2A] hover:bg-[#F1EFE8]"
                }`}
              >
                {busy === tier.id ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {tier.cta}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {/* Value prop callout */}
        <div className="mt-10 rounded-[12px] bg-[#F1EFE8] border border-[#D3D1C7] px-8 py-6 flex flex-col sm:flex-row items-center gap-4">
          <Zap className="w-5 h-5 text-[#534AB7] shrink-0" />
          <p className="text-sm text-[#2C2C2A] leading-relaxed">
            <strong>Freelance formatters charge £50+ per cover.</strong> If PublishMate saves you even two projects a month, the Pro plan pays for itself — and then some.
          </p>
        </div>

        <p className="text-center text-xs text-[#5F5E5A] tracking-wide mt-10">
          Need help choosing?{" "}
          <Link to="/" className="underline hover:text-[#534AB7]">
            See all features
          </Link>
        </p>

        <div className="mt-8 max-w-md mx-auto bg-white border border-[#D3D1C7] rounded-[12px] p-5" data-testid="pricing-promo-section">
          {session ? (
            <PromoCodeInput onApplied={() => refreshProfile?.()} />
          ) : (
            <p className="text-center text-xs text-[#5F5E5A]">
              Have a promo code? <Link to="/signup" className="text-[#534AB7] underline">Sign up</Link> first, then redeem it on your account.
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default Pricing;
